import { useState } from 'react';
import { Box, Container, Typography, Tab, Tabs, Stack } from '@mui/material';
import { AppBar } from '../components/AppBar';
import { CodeBlock } from '../components/CodeBlock';
import { DocsNav } from '../components/docs/DocsNav';
import { DocsSection } from '../components/docs/DocsSection';
import { FieldTable, inlineCode } from '../components/docs/FieldTable';
import {
  AUTH_SETTINGS,
  BILLING_RULES,
  CONFIDENCE_GRADES,
  ENDPOINTS,
  HTTP_ERRORS,
  JOB_FIELDS,
  MCP_TOOLS,
  POST_RESPONSE_FIELDS,
  REQUEST_FIELDS,
  RESULT_FIELDS,
  SECTIONS,
  STATUSES,
} from '../components/docs/content';
import {
  JOB_RESPONSES,
  MCP_INSTALL,
  MCP_URL,
  POST_RESPONSES,
  REQUEST_BODY,
  REQUEST_EXAMPLES,
} from '../components/docs/snippets';

import activeSample from '../components/samples/active.json';
import emptyPaymentsSample from '../components/samples/empty-payments-store.json';
import deletedStoreSample from '../components/samples/deleted-store.json';
import inactiveSample from '../components/samples/inactive-or-maintenance.json';
import platformNotSupportedSample from '../components/samples/platform-not-supported.json';
import platformUnknownSample from '../components/samples/platform-unknown.json';
import platformNoPaymentsSample from '../components/samples/platform-identified-no-payments.json';
import notMerchantSample from '../components/samples/not-a-merchant.json';
import unreachableSample from '../components/samples/unreachable-dns.json';
import unreachableSslSample from '../components/samples/unreachable-ssl.json';
import unreachableAntiBotSample from '../components/samples/unreachable-anti-bot.json';
import unreachableGeoSample from '../components/samples/unreachable-geo.json';
import unreachableOtherSample from '../components/samples/unreachable-other.json';
import errorSample from '../components/samples/error.json';

const RESPONSE_TABS: Array<{ label: string; payload: unknown }> = [
  { label: 'active', payload: activeSample },
  { label: 'empty_payments_store', payload: emptyPaymentsSample },
  { label: 'deleted_store', payload: deletedStoreSample },
  { label: 'inactive_or_maintenance', payload: inactiveSample },
  { label: 'platform_not_supported', payload: platformNotSupportedSample },
  { label: 'platform_unknown', payload: platformUnknownSample },
  { label: 'platform_identified_no_payments', payload: platformNoPaymentsSample },
  { label: 'not_a_merchant', payload: notMerchantSample },
  { label: 'unreachable_dns', payload: unreachableSample },
  { label: 'unreachable_ssl', payload: unreachableSslSample },
  { label: 'unreachable_anti_bot', payload: unreachableAntiBotSample },
  { label: 'unreachable_geo', payload: unreachableGeoSample },
  { label: 'unreachable_other', payload: unreachableOtherSample },
  { label: 'error', payload: errorSample },
];

const tabSx = { fontFamily: 'IBM Plex Mono', fontSize: '0.75rem', textTransform: 'none' } as const;
const muted = { color: 'var(--ink-muted)' } as const;

/** A sub-heading inside a section (Request / Responses / …). */
function SubHeading({ children }: { children: string }) {
  return (
    <Typography variant="h4" component="h3" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );
}

/** A body paragraph that renders `code spans`. */
function Para({ text, mb = 1 }: { text: string; mb?: number }) {
  return (
    <Typography variant="body2" sx={{ ...muted, mb, maxWidth: 640 }}>
      {inlineCode(text)}
    </Typography>
  );
}

export function Docs() {
  const [sampleTab, setSampleTab] = useState(0);
  const [reqTab, setReqTab] = useState(0);
  const [mcpTab, setMcpTab] = useState(0);

  return (
    <Box>
      <AppBar />
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Box sx={{ mb: { xs: 5, md: 7 } }}>
          <Typography variant="h1" sx={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
            API docs
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, maxWidth: 560, ...muted }}>
            Everything needed to call the identify API: authentication, both endpoints, every
            field of the result, all statuses, errors, MCP, and how billing works.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 10 } }}>
          <DocsNav sections={SECTIONS} />
          <Box component="main" sx={{ flex: 1, minWidth: 0, maxWidth: 760, order: { md: -1 } }}>
            <Stack spacing={7}>
              <DocsSection id="overview" title="Overview">
                <Para text="The identify API answers one question: which payment methods does a storefront accept. The base URL is `https://api.acceptlens.com`; every request carries an OAuth Bearer token." />
                <FieldTable label="Endpoints" headers={['Endpoint', 'Purpose']} rows={ENDPOINTS} />
                <Para text="Scans are asynchronous and no call ever blocks on one: when a result for the domain is already available it returns immediately, otherwise a scan is queued. A cold scan usually resolves in 30–90 seconds." />
              </DocsSection>

              <DocsSection id="authentication" title="Authentication">
                <Para text="Create an OAuth client on your dashboard — the `client_secret` is shown once and can only be rotated afterwards. Exchange it for an access token (`client_credentials` grant) and send the token on every call; there is no API-key path." />
                <FieldTable label="Authentication settings" headers={['Setting', 'Value']} rows={AUTH_SETTINGS} />
              </DocsSection>

              <DocsSection id="quick-start" title="Quick start">
                <Para text="End to end in four languages: mint a token, submit the store, poll until done." />
                <Tabs value={reqTab} onChange={(_, v) => setReqTab(v)} variant="scrollable" scrollButtons="auto">
                  {REQUEST_EXAMPLES.map((e) => (
                    <Tab key={e.label} label={e.label} sx={tabSx} />
                  ))}
                </Tabs>
                <CodeBlock code={REQUEST_EXAMPLES[reqTab].code} language={REQUEST_EXAMPLES[reqTab].language} />
              </DocsSection>

              <DocsSection id="post-identify" title="POST /v1/identify">
                <Para text="Submits a store URL for identification." />
                <SubHeading>Request body</SubHeading>
                <FieldTable label="Request fields" headers={['Field', 'Type', 'Description']} rows={REQUEST_FIELDS} />
                <CodeBlock code={REQUEST_BODY} language="json" copyable={false} />
                <SubHeading>Responses</SubHeading>
                <Para text="`200` when a result for the domain is already available — the envelope carries it. `202` when a scan was queued — poll the job." />
                <FieldTable
                  label="Response fields"
                  headers={['Field', 'Type', 'Description']}
                  rows={POST_RESPONSE_FIELDS}
                />
                <CodeBlock code={POST_RESPONSES} language="javascript" copyable={false} />
              </DocsSection>

              <DocsSection id="get-job" title="GET /v1/jobs/{job_id}">
                <Para text="Collects the result of a queued scan. Each call checks once and returns immediately — poll every few seconds until `status` is `succeeded` or `failed`. Job records live 24 hours and are scoped to your client: another caller's `job_id` reads as `404`." />
                <FieldTable label="Job fields" headers={['Field', 'Type', 'Description']} rows={JOB_FIELDS} />
                <CodeBlock code={JOB_RESPONSES} language="javascript" copyable={false} />
              </DocsSection>

              <DocsSection id="result" title="The result object">
                <Para text="The body of an immediate `200` and the `result` field of a succeeded job. It carries only what a caller needs to act on the answer — internal detection detail is not part of the contract." />
                <FieldTable label="Result fields" headers={['Field', 'Type', 'Description']} rows={RESULT_FIELDS} />
              </DocsSection>

              <DocsSection id="confidence" title="Confidence grades">
                <Para text="Every accepted method carries the strength of the evidence behind it." />
                <FieldTable label="Confidence grades" headers={['Grade', 'Meaning']} rows={CONFIDENCE_GRADES} />
                <Para text="An advertised buy-now-pay-later widget alone is never a grade: it lands in `bnpl_advertised`, not in `payments`." />
              </DocsSection>

              <DocsSection id="statuses" title="Status reference">
                <Para text="Every value the result's `status` field can take." />
                <FieldTable label="Status reference" headers={['Status', 'Meaning']} rows={STATUSES} />
                <Para text="Only `active` carries entries in `payments`. `unreachable_*` and `error` are inconclusive and never billed — submit the URL again later." />
              </DocsSection>

              <DocsSection id="examples" title="Sample responses">
                <Para text="One sample per status, in the exact shape the API returns." />
                <Tabs value={sampleTab} onChange={(_, v) => setSampleTab(v)} variant="scrollable" scrollButtons="auto">
                  {RESPONSE_TABS.map((t) => (
                    <Tab key={t.label} label={t.label} sx={tabSx} />
                  ))}
                </Tabs>
                <CodeBlock
                  code={JSON.stringify(RESPONSE_TABS[sampleTab].payload, null, 2)}
                  language="json"
                  copyable={false}
                />
              </DocsSection>

              <DocsSection id="errors" title="HTTP errors">
                <FieldTable label="HTTP errors" headers={['Code', 'Error', 'Meaning']} rows={HTTP_ERRORS} />
              </DocsSection>

              <DocsSection id="mcp" title="MCP for agents">
                <Para
                  text={`The same API is available to agents at \`${MCP_URL}\` over the Streamable HTTP transport, authenticated with the same OAuth tokens.`}
                />
                <FieldTable label="MCP tools" headers={['Tool', 'Input', 'Returns']} rows={MCP_TOOLS} />
                <Para text="Clients that speak MCP OAuth (Claude Code, claude.ai, ChatGPT, Cursor, VS Code) discover the sign-in flow from the server — add the URL and authenticate in the browser. Clients that only send a static header (Codex CLI, Gemini CLI, opencode) need a token minted from an OAuth client on your dashboard; tokens expire after one hour." />
                <Tabs value={mcpTab} onChange={(_, v) => setMcpTab(v)} variant="scrollable" scrollButtons="auto">
                  {MCP_INSTALL.map((e) => (
                    <Tab key={e.label} label={e.label} sx={tabSx} />
                  ))}
                </Tabs>
                <CodeBlock code={MCP_INSTALL[mcpTab].code} language={MCP_INSTALL[mcpTab].language} />
              </DocsSection>

              <DocsSection id="billing" title="Billing">
                <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', rowGap: 1 }}>
                  {BILLING_RULES.map((rule) => (
                    <Typography key={rule} component="li" variant="body2" sx={{ ...muted, maxWidth: 640 }}>
                      {inlineCode(rule)}
                    </Typography>
                  ))}
                </Box>
              </DocsSection>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
